import { spacing } from '@scality/core-ui';
import type React from 'react';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { useChat } from '../hooks/useChat';
import { useScrollbarWidth } from '../hooks/useScrollbarWidth';
import { AgentLandingPage } from './chat-messages/AgentLandingPage';
import { ChatCard } from './chat-messages/ChatCard';
import { GuardianContext } from './GuardianProvider';
import { MessageComposer } from './message-composer/MessageComposer';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  overflow: hidden;
`;

const ChatCardContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
  height: 100%;
  overflow-y: auto;
  /* Spans the whole column — so the scrollbar sits at its edge — and the gutters center the
     cards inside it. The top padding clears the navbar the cards scroll behind. */
  width: 100%;
  padding-inline: var(--chat-gutter);
  padding-top: calc(var(--header-height) + var(--chat-top-margin));
  padding-bottom: ${spacing.r24};

  transform: translateY(
    ${spacing.f8}
  ); // Fixes the chat not going behind the corner radius
`;

type ChatProps = {
  /** Show the composer's agent/instance selectors. Off in dashboard mode. */
  showComposerContext?: boolean;
};

export const Chat: React.FC<ChatProps> = ({ showComposerContext = true }) => {
  const { selectedAgent } = useContext(GuardianContext);
  const { scrollBarWidth, handleScrollbarWidth } = useScrollbarWidth();
  const { conversation, sendMessage, isResponding, rendering } = useChat();
  const submitHandler = useCallback((query: string) => sendMessage({ query }), [sendMessage]);

  const shouldShowLandingPage = selectedAgent && !conversation.hasAssistantReplied;

  return (
    <ChatContainer>
      <ChatCardContainer ref={handleScrollbarWidth}>
        {shouldShowLandingPage && selectedAgent && (
          <AgentLandingPage
            landingPage={selectedAgent.landingPage}
            agentIcon={selectedAgent.icon}
            agentName={selectedAgent.name}
          />
        )}
        {conversation.messages.map((message, index, arr) => (
          <ChatCard
            key={message.timestamp.toISOString()}
            response={message}
            isLastResponse={rendering.isThinking ? index === arr.length - 2 : index === arr.length - 1}
            isThinking={rendering.isThinking && index === arr.length - 1}
            shouldAnimate={index === arr.length - 1 && index >= rendering.historisedMessageCount}
          />
        ))}
      </ChatCardContainer>
      <MessageComposer
        onSubmit={submitHandler}
        isLoading={isResponding}
        scrollBarWidth={scrollBarWidth}
        showContext={showComposerContext}
      />
    </ChatContainer>
  );
};
